import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Mail, Phone, Headphones } from 'lucide-react';
import { Card, CardContent } from './ui/card';

const CustomerService = () => {
  const { language } = useLanguage();

  const customerServiceTeam = [
  {
    id: 1,
    name: "Fiorella Carrasquel",
    origin: { city: "Caracas", country: "Venezuela" },
    email: "fcarrasquel@palerminsurance.com",
    phone: "+14694348287",
    phoneDisplay: "+1 (469) 434-8287",
    isOfficePhone: true,
    photo: "/images/team/fiorella-carrasquel.png"
  },
  {
    id: 2,
    name: "Renan Carrillo",
    origin: { city: "San Cristóbal", country: "Venezuela" },
    email: "rcarrillo@palerminsurance.com",
    phone: "+14694348287",
    phoneDisplay: "+1 (469) 434-8287",
    isOfficePhone: true,
    photo: "/images/team/renan-carrillo.png"
  }
];

const itServiceTeam = [
  {
    id: 1,
    name: "Roberto Silva",
    origin: { city: "Carolina", country: "Puerto Rico" },
    email: "rsilva@palerminsurance.com",
    phone: "+14694348287",
    phoneDisplay: "+1 (469) 434-8287",
    isOfficePhone: true,
    npn: null,
    photo: "/images/team/roberto-silva.png"
  }
];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <section className="bg-gradient-to-r from-green-700 to-green-900 text-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block bg-white/10 p-4 rounded-full mb-6">
              <Headphones className="w-16 h-16" />
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              {language === 'en' ? 'Customer Service' : 'Servicio al Cliente'}
            </h1>
            <p className="text-xl md:text-2xl text-green-100 mb-8">
              {language === 'en' 
                ? 'Our Support Team is Here to Help' 
                : 'Nuestro Equipo de Soporte Está Aquí para Ayudar'}
            </p>
            <p className="text-lg text-green-50">
              {language === 'en'
                ? 'Our customer service professionals are dedicated to providing excellent support for your insurance needs. They can assist with general inquiries, policy questions, and administrative matters.'
                : 'Nuestros profesionales de servicio al cliente están dedicados a proporcionar excelente soporte para sus necesidades de seguro. Pueden asistir con consultas generales, preguntas de póliza y asuntos administrativos.'}
            </p>
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="mb-12 text-center bg-blue-50 p-6 rounded-lg max-w-4xl mx-auto">
            <p className="text-sm text-gray-700">
              <svg className="inline-block w-5 h-5 text-blue-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <strong>{language === 'en' ? 'Note:' : 'Nota:'}</strong>{' '}
              {language === 'en'
                ? 'Customer service team members provide administrative support and are not licensed insurance agents.'
                : 'Los miembros del equipo de servicio al cliente proporcionan soporte administrativo y no son agentes de seguros con licencia.'}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {customerServiceTeam.map((member) => (
              <Card
                key={member.id}
                className="hover:shadow-xl transition-all duration-300 border-none bg-white"
              >
                  <CardContent className="p-6">
                    {member.photo ? (
                      <div className="w-24 h-24 mx-auto mb-4 rounded-full overflow-hidden border-4 border-blue-100">
                        <img 
                          src={member.photo} 
                          alt={member.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ) : (
                      <div className="w-24 h-24 bg-gradient-to-br from-blue-600 to-blue-700 rounded-full flex items-center justify-center mx-auto mb-4">
                        <span className="text-3xl font-bold text-white">
                          {member.name.split(' ').map(n => n[0]).join('').substring(0, 2)}
                        </span>
                      </div>
                    )}

                    <h3 className="text-xl font-bold text-gray-900 text-center mb-2">
                      {member.name}
                    </h3>

                    <p className="text-sm text-gray-600 text-center mb-4">
                      <span className="font-semibold">{language === 'en' ? 'From:' : 'De:'}</span>{' '}
                      {member.origin.city}, {member.origin.country}
                    </p>

                    <div className="space-y-3">
                      {member.email && (
                        <a
                          href={`mailto:${member.email}`}
                          className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 transition-colors"
                        >
                          <Mail className="w-4 h-4 flex-shrink-0" />
                          <span className="break-all">{member.email}</span>
                        </a>
                      )}

                      {member.phone && (
                        <a
                          href={`tel:${member.phone}`}
                          className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 transition-colors"
                        >
                          <Phone className="w-4 h-4 flex-shrink-0" />
                          <span>
                            {member.isOfficePhone 
                              ? (language === 'en' ? 'Call our office: ' : 'Llamar a la oficina: ')
                              : ''}{member.phoneDisplay}
                          </span>
                        </a>
                      )}

                      {!member.phone && (
                        <p className="text-sm text-gray-500 italic text-center">
                          {language === 'en' ? 'Contact via main office' : 'Contactar vía oficina principal'}
                        </p>
                      )}
                    </div>
                  </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {language === 'en' ? 'IT Service' : 'Servicio de IT'}
            </h2>
            <p className="text-lg text-gray-600">
              <span className="font-semibold">{language === 'en' ? 'Technical Support' : 'Soporte Técnico'}</span>
            </p>
            <p className="text-gray-600 mt-2">
              {language === 'en'
                ? 'Our IT service professional provides technical support and maintains our systems to ensure seamless service delivery.'
                : 'Nuestro profesional de servicio de TI proporciona soporte técnico y mantiene nuestros sistemas para garantizar una prestación de servicios sin problemas.'}
            </p>
          </div>

          <div className="mb-8 text-center bg-blue-50 p-6 rounded-lg max-w-4xl mx-auto">
            <p className="text-sm text-gray-700">
              <svg className="inline-block w-5 h-5 text-blue-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <strong>{language === 'en' ? 'Note:' : 'Nota:'}</strong>{' '}
              {language === 'en'
                ? 'IT service team member provides technical support and is not a licensed insurance agent.'
                : 'El miembro del equipo de servicio de TI proporciona soporte técnico y no es un agente de seguros con licencia.'}
            </p>
          </div>

          <div className="flex justify-center">
            <div className="w-full max-w-sm">
              {itServiceTeam.map((member) => (
                <Card
                  key={member.id}
                  className="hover:shadow-xl transition-all duration-300 border-none bg-white"
                >
                  <CardContent className="p-6">
                    {member.photo ? (
                      <div className="w-24 h-24 mx-auto mb-4 rounded-full overflow-hidden border-4 border-blue-100">
                        <img 
                          src={member.photo} 
                          alt={member.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ) : (
                      <div className="w-24 h-24 bg-gradient-to-br from-blue-600 to-blue-700 rounded-full flex items-center justify-center mx-auto mb-4">
                        <span className="text-3xl font-bold text-white">
                          {member.name.split(' ').map(n => n[0]).join('').substring(0, 2)}
                        </span>
                      </div>
                    )}

                    <h3 className="text-xl font-bold text-gray-900 text-center mb-2">
                      {member.name}
                    </h3>

                    <p className="text-sm text-gray-600 text-center mb-4">
                      <span className="font-semibold">{language === 'en' ? 'From:' : 'De:'}</span>{' '}
                      {member.origin.city}, {member.origin.country}
                    </p>

                    <div className="space-y-3">
                      {member.email && (
                        <a
                          href={`mailto:${member.email}`}
                          className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 transition-colors justify-center"
                        >
                          <Mail className="w-4 h-4 flex-shrink-0" />
                          <span className="break-all">{member.email}</span>
                        </a>
                      )}

                      {member.phone && (
                        <a
                          href={`tel:${member.phone}`}
                          className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 transition-colors justify-center"
                        >
                          <Phone className="w-4 h-4 flex-shrink-0" />
                          <span>
                            {member.isOfficePhone 
                              ? (language === 'en' ? 'Call our office: ' : 'Llamar a la oficina: ')
                              : ''}{member.phoneDisplay}
                          </span>
                        </a>
                      )}

                      {!member.phone && (
                        <p className="text-sm text-gray-500 italic text-center">
                          {language === 'en' ? 'Contact via main office' : 'Contactar vía oficina principal'}
                        </p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CustomerService;